import Vue from 'vue';
import { opportunityLlmExtractionsService } from '@/services/opportunity-llm-extractions.service';
import { hasExtractionEnded } from '@/models/extraction-steps';
import { followLlmJob } from '@/util/llm-job-stream';

// AbortControllers for in-flight extraction job SSE streams, one per
// opportunity (non-serializable, kept outside state — an AbortController
// can't be reconstructed from a Vuex snapshot, so keeping them here keeps
// devtools time-travel safe). Per-opportunity — like form-fill's
// fillControllers — so a running job keeps streaming (and its header pill
// keeps updating) while the user navigates to other pages.
const jobControllers = new Map();

// Replace an opportunity's job controller, aborting its previous stream.
const newJobController = (opportunityId) => {
  const existing = jobControllers.get(opportunityId);
  if (existing) existing.abort();
  const controller = new AbortController();
  jobControllers.set(opportunityId, controller);
  return controller;
};

const clearJobController = (opportunityId) => {
  const controller = jobControllers.get(opportunityId);
  if (!controller) return;
  controller.abort();
  jobControllers.delete(opportunityId);
};

// Controller for the page-level watch flow (queue polling + latest-job
// lookup) of the opportunity being viewed. Replaced on every page entry;
// aborting it does NOT stop job streams.
let watchController = null;

// Form-fill jobs share the jobs endpoint but are watched by the formFill
// module — this watcher must only ever pick up extraction jobs.
const isExtractionJob = (job) =>
  !!job && (job.job_type === 'fields_extraction' || job.job_type === 'requirements_extraction');

function createStreamCallbacks({ jobType, state, commit, dispatch, opportunityId, signal, jobId }) {
  const guard = () => !signal.aborted;
  // Page-facing modules (fieldsExtraction / requirementsExtraction) hold the
  // state of the opportunity currently being viewed — a background job's
  // events must not leak into them, only into its session (header pill).
  const watched = () => state.watchedOpportunityId === opportunityId;
  return {
    onStatus(data) {
      if (!guard()) return;
      if (data && data.step) commit('SET_SESSION', { opportunityId, step: data.step });
      if (watched()) dispatch(`${jobType}/onProgress`, { jobId, ssePayload: data }, { root: true });
    },
    onComplete(data) {
      if (guard())
        dispatch('_onEnd', { jobType, opportunityId, jobId, status: 'COMPLETED', ssePayload: data, error: null });
    },
    onError(err) {
      if (guard())
        dispatch('_onEnd', { jobType, opportunityId, jobId, status: 'FAILED', ssePayload: null, error: err });
    },
    onCancelled() {
      if (guard())
        dispatch('_onEnd', { jobType, opportunityId, jobId, status: 'CANCELLED', ssePayload: null, error: null });
    },
    onFieldResult(data) {
      if (guard() && watched()) dispatch(`${jobType}/onFieldResult`, { jobId, fieldResult: data }, { root: true });
    },
  };
}

const initialState = () => ({
  // The opportunity whose pages are being viewed; only its job's events reach
  // the page-facing fields/requirements modules.
  watchedOpportunityId: null,
  // Live extraction jobs by opportunity (drives the header task pills):
  // { [opportunityId]: { jobId, jobType, opportunityName, step } }.
  sessions: {},
});

const state = initialState();

const mutations = {
  SET_WATCHED_OPPORTUNITY(state, opportunityId) {
    state.watchedOpportunityId = opportunityId;
  },
  // Merges into the existing session so partial updates (e.g. `step` from a
  // status event) keep the seeded name/jobId intact.
  SET_SESSION(state, { opportunityId, ...session }) {
    const existing = state.sessions[opportunityId] || {};
    Vue.set(state.sessions, opportunityId, { ...existing, ...session });
  },
  CLEAR_SESSION(state, opportunityId) {
    Vue.delete(state.sessions, opportunityId);
  },
};

const getters = {
  // Running extraction jobs as header task entries.
  extractionTasks: (state) =>
    Object.keys(state.sessions).map((opportunityId) => ({ opportunityId, ...state.sessions[opportunityId] })),
};

const actions = {
  // Stop watching the current page's opportunity: its watch flow, job stream
  // and header session. Background jobs of other opportunities are untouched.
  abort({ state, commit }) {
    if (watchController) watchController.abort();
    watchController = null;
    if (state.watchedOpportunityId != null) {
      clearJobController(state.watchedOpportunityId);
      commit('CLEAR_SESSION', state.watchedOpportunityId);
    }
  },

  async watchExtractionJob(
    { commit, dispatch, rootGetters },
    { opportunityId, controller: existingController = null },
  ) {
    // Stop the previous page's watch flow only — a job still running there
    // keeps streaming in the background and stays in the header pills.
    if (watchController) watchController.abort();
    const controller = existingController || new AbortController();
    watchController = controller;
    const signal = controller.signal;

    commit('SET_WATCHED_OPPORTUNITY', opportunityId);
    commit('fieldsExtraction/RESET', null, { root: true });
    commit('requirementsExtraction/RESET', null, { root: true });

    try {
      // get queue messages, if queue is not empty, then wait until queue is empty
      await dispatch('opportunityDocumentsQueue/getOpportunityQueuedDocuments', { opportunityId }, { root: true });
      if (signal.aborted) return;
      if (rootGetters['opportunityDocumentsQueue/hasScheduledKeywordIndexing']) {
        await dispatch('fieldsExtraction/onQueued', null, { root: true });
        await new Promise((resolve) => {
          const onAbort = () => resolve();
          signal.addEventListener('abort', onAbort, { once: true });
          dispatch(
            'opportunityDocumentsQueue/pollForQueuedDocuments',
            {
              opportunityId,
              signal,
              onKeywordIndexerDone: () => {
                signal.removeEventListener('abort', onAbort);
                resolve();
              },
            },
            { root: true },
          );
        });
      }
      if (signal.aborted) return;

      // Queue is now empty — get the newest extraction job. The `job_type`
      // query param is single-valued, so fetch the opportunity's job list
      // (newest-first) and skip past any form-fill jobs in it.
      const jobs = await opportunityLlmExtractionsService.getJobs({ opportunityId, signal });
      if (signal.aborted) return;
      const latest = jobs.find(isExtractionJob) || null;
      if (!latest) return;

      const jobType = latest.job_type === 'requirements_extraction' ? 'requirementsExtraction' : 'fieldsExtraction';

      if (latest.status === 'PENDING' || latest.status === 'PROCESSING') {
        await dispatch('subscribe', {
          jobType,
          opportunityId,
          opportunityName: latest.opportunity_name,
          jobId: latest.id,
        });
        return;
      }

      if (hasExtractionEnded(latest.status)) {
        dispatch('_onJobResultAvailable', { jobType, opportunityId, jobResult: latest });
      }
      // FAILED / CANCELLED / unknown — no-op.
    } catch {
      console.log('error');
      // silent — user can still trigger manually
    }
  },

  // On app load, re-attach to each of the user's running extraction jobs so
  // the header's task pills survive a refresh on any page (mirrors
  // formFill/watchActiveFillJobs). The `status` param is single-valued, hence
  // one query per running state; form-fill jobs have their own watcher. One
  // job per opportunity, keeping the first seen: lists are newest-first, and
  // PENDING (a newer queued job) outranks PROCESSING.
  async watchActiveExtractionJobs({ dispatch }) {
    const results = await Promise.all(
      ['PENDING', 'PROCESSING'].map((status) => opportunityLlmExtractionsService.getJobs({ status }).catch(() => [])),
    );
    const jobsByOpportunity = new Map();
    results.flat().forEach((job) => {
      if (isExtractionJob(job) && job.opportunity_id && !jobsByOpportunity.has(job.opportunity_id)) {
        jobsByOpportunity.set(job.opportunity_id, job);
      }
    });
    await Promise.all(
      [...jobsByOpportunity.values()].map((job) =>
        dispatch('subscribe', {
          jobType: job.job_type === 'requirements_extraction' ? 'requirementsExtraction' : 'fieldsExtraction',
          opportunityId: job.opportunity_id,
          opportunityName: job.opportunity_name,
          jobId: job.id,
        }),
      ),
    );
  },

  async subscribe({ state, commit, dispatch }, { jobType, opportunityId, opportunityName = null, jobId }) {
    // Replaces any previous stream for this opportunity, so re-entering a
    // page (or the app-load watcher racing the page watcher) can't stack
    // duplicate subscriptions to the same job.
    const { signal } = newJobController(opportunityId);

    // Only set the name when known: the fields → requirements chain carries
    // none, but the job is for the same opportunity so the session's current
    // name stays valid.
    commit('SET_SESSION', {
      opportunityId,
      jobId,
      jobType,
      step: null,
      ...(opportunityName ? { opportunityName } : {}),
    });

    const watched = () => state.watchedOpportunityId === opportunityId;
    if (watched()) await dispatch(`${jobType}/onConnecting`, { opportunityId, jobId }, { root: true });
    if (signal.aborted) return;

    followLlmJob({
      jobId,
      signal,
      sseCallbacks: createStreamCallbacks({ jobType, state, commit, dispatch, opportunityId, signal, jobId }),
      onEndedResult: (jobResult) => dispatch('_onJobResultAvailable', { jobType, opportunityId, jobResult }),
    });

    if (signal.aborted) return;
    if (watched()) await dispatch(`${jobType}/onStreaming`, { opportunityId, jobId }, { root: true });
  },

  /* Private Actions */
  async _onEnd({ state, commit, dispatch }, { jobType, opportunityId, jobId, status, ssePayload, error }) {
    const session = state.sessions[opportunityId];
    clearJobController(opportunityId);
    commit('CLEAR_SESSION', opportunityId);

    if (state.watchedOpportunityId === opportunityId) {
      // if last job was requirements extraction, then get the data of the latest fields extraction too
      if (jobType === 'requirementsExtraction') {
        const fieldsExtractionJobResult = await opportunityLlmExtractionsService.getLatestExtraction({
          opportunityId,
          jobType: 'fields_extraction',
        });
        await dispatch(
          `fieldsExtraction/onJobResultAvailable`,
          { jobResult: fieldsExtractionJobResult, opportunityId },
          { root: true },
        );
        await dispatch(
          `requirementsExtraction/onEnd`,
          { jobId, status, ssePayload, error, opportunityId },
          { root: true },
        );
      } else {
        await dispatch(`fieldsExtraction/onEnd`, { jobId, status, ssePayload, error, opportunityId }, { root: true });
      }
    }

    // A completed fields extraction can chain a requirements extraction job;
    // follow it so the header pill (and the page, when watched) tracks the
    // whole run.
    const nextJobId =
      jobType === 'fieldsExtraction' && status === 'COMPLETED' && ssePayload && ssePayload.meta
        ? ssePayload.meta.requirements_extraction_job_id
        : null;
    if (nextJobId) {
      await dispatch('subscribe', {
        jobType: 'requirementsExtraction',
        opportunityId,
        opportunityName: session ? session.opportunityName : null,
        jobId: nextJobId,
      });
    }
  },

  async _onJobResultAvailable({ state, commit, dispatch }, { jobType, opportunityId, jobResult }) {
    clearJobController(opportunityId);
    commit('CLEAR_SESSION', opportunityId);

    if (state.watchedOpportunityId !== opportunityId) return;

    // if last job was requirements extraction, then get the data of the latest fields extraction too
    if (jobType === 'requirementsExtraction') {
      const fieldsExtractionJobResult = await opportunityLlmExtractionsService.getLatestExtraction({
        opportunityId,
        jobType: 'fields_extraction',
      });
      await dispatch(
        `fieldsExtraction/onJobResultAvailable`,
        { jobResult: fieldsExtractionJobResult, opportunityId },
        { root: true },
      );
      await dispatch(`requirementsExtraction/onJobResultAvailable`, { jobResult, opportunityId }, { root: true });
    } else {
      await dispatch(`fieldsExtraction/onJobResultAvailable`, { jobResult, opportunityId }, { root: true });
    }
  },
};

export const extractionJob = {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
